import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, withModifiers as _withModifiers, Teleport as _Teleport, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-38e8d6d4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "screen-wrapper" }
const _hoisted_2 = { class: "is-flex is-align-items-center is-justify-content-space-between mb-5" }
const _hoisted_3 = {
  key: 0,
  class: "mb-0"
}
const _hoisted_4 = {
  key: 1,
  class: "mb-0"
}
const _hoisted_5 = { class: "form" }
const _hoisted_6 = { class: "field" }
const _hoisted_7 = { class: "field" }
const _hoisted_8 = { class: "field" }
const _hoisted_9 = { class: "field" }
const _hoisted_10 = {
  key: 1,
  class: "info"
}

import { hexToNumber, numberToHex } from 'web3-utils'
import { computed, onMounted, onUnmounted, ref, toRaw } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Notification from '@/popup/components/shared/Notification.vue'
import IconButton from '@/popup/components/shared/IconButton.vue'
import Button from '@/popup/components/shared/Button.vue'
import { GlobalDatabase, saveLastState } from '@/popup/storage/Database'
import {
  numberOrHexValidator,
  urlValidator,
} from '@/popup/components/shared/Form/validators'
import { Input, Error, Label } from '@/popup/components/shared/Form/Form'
import DropdownMultiple from '@/popup/components/shared/Dropdown/DropdownMultiple.vue'
import { useAppStore } from '@/popup/store/AppStore'
import RelayerDeleteModal from '@/popup/components/Modal/RelayerDelete.vue'
import { t } from '@/popup/lib/i18n'
import { useGlobals } from '@/popup/compositions/useGlobals'
import { removeTrailingSlash } from '@/popup/components/shared/Form/formatters'
import { InternalMethods } from '@/shared/apis/messages'
import Spinner from '@/popup/components/shared/Spinner.vue'
import { getErrorMessage } from '@/shared/validators'
import { InputError } from '@/popup/components/shared/Form/FormTypes'
import { DropdownProfile } from '@/shared/models/Profile'
import ModalTransition from '@/popup/components/shared/Modal/ModalTransition.vue'
import { RelayServiceDBSchema } from '@/shared/Database/UPSettings/models'

type ChainIdObject = {
  id: number
  label: string
}

type Errors = {
  name: InputError[]
  apiUrl: InputError[]
  chainIds: InputError[]
  website: InputError[]
}

type Relayer = {
  id?: string
  name: string
  apiURL: string
  chainIDs: number[]
  website?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'AddRelayer',
  setup(__props) {

const relayer = ref<Relayer>({
  id: '',
  name: '',
  apiURL: '',
  chainIDs: [],
})

const errors = ref<Errors>({
  name: [],
  apiUrl: [],
  chainIds: [],
  website: [],
})

const name = ref<InstanceType<typeof Input>>()
const apiUrl = ref<InstanceType<typeof Input>>()
const website = ref<InstanceType<typeof Input>>()

const router = useRouter()
const route = useRoute()
const appStore = useAppStore()
const relayerId = route.params.relayerId as string | undefined
const isDeleteModalOpen = ref(false)
const isEdit = !!relayerId
const focus = ref(false)
const allChains = ref<ChainIdObject[]>([])
const allProfiles = ref<DropdownProfile[]>([])
const selectedChainIds = ref<ChainIdObject[]>([])
const allRelayers = ref<RelayServiceDBSchema[]>([])
const isSubmitting = ref(false)

const createChainIdObject = (
  chainId: string | number
): { id: number; label: string } => {
  return {
    id: Number(chainId),
    label: `${hexToNumber(chainId)}`,
  }
}

const setRelayService = (relayService: RelayServiceDBSchema) => {
  relayer.value = {
    ...relayService,
    apiURL: relayService.apiUrl,
    chainIDs: relayService.chainIds.map(Number),
  }
}

onMounted(async () => {
  // recover from saved state
  if (appStore.lastPopupState?.relayer) {
    relayer.value = appStore.lastPopupState.relayer
  } else if (isEdit) {
    const dbRelayer = await GlobalDatabase.getRelayerById(relayerId)

    if (dbRelayer) {
      setRelayService(dbRelayer)
    }
  }

  const allNetworks = await useGlobals().sendMessage({
    method: InternalMethods.GET_NETWORKS,
    payload: undefined,
  })
  allChains.value = allNetworks.map(network => {
    const chainIdHex = numberToHex(network.chainId)
    const chainIdObject = createChainIdObject(chainIdHex)

    chainIdObject.label = network.name + ' - ' + chainIdObject.label

    return chainIdObject
  })
  allProfiles.value = appStore.dropdownAddresses

  selectedChainIds.value = appStore.lastPopupState?.selectedChainIds
    ? JSON.parse(appStore.lastPopupState.selectedChainIds)
    : relayer.value.chainIDs.map(chainIdHex => createChainIdObject(chainIdHex))

  allRelayers.value = await GlobalDatabase.getAllRelayers()

  focus.value = true
})

const canSubmit = computed(
  () =>
    !!relayer.value?.name &&
    !!relayer.value?.apiURL &&
    !errors.value?.name.length &&
    !errors.value?.apiUrl.length &&
    !errors.value?.website.length
)

const validate = () => {
  name.value?.validate(relayer.value?.name)
  apiUrl.value?.validate(relayer.value?.apiURL)
  website.value?.validate(relayer.value?.website || '')
}

const save = async () => {
  validate()
  if (!canSubmit.value) {
    return
  }
  isSubmitting.value = true

  const method = isEdit
    ? InternalMethods.UPDATE_RELAY_SERVICE
    : InternalMethods.ADD_RELAY_SERVICE

  const updatedRelayer = {
    ...relayer.value,
    apiURL: normalizeUrl(relayer.value.apiURL),
    chainIDs: selectedChainIds.value.map(chainIdObject => chainIdObject.id),
  }

  const response = await useGlobals().sendMessage({
    method: method,
    payload: updatedRelayer,
  })

  isSubmitting.value = false

  if (response.success) {
    await router.push({ name: 'listRelayer' })
  } else {
    errors.value.apiUrl.push(getErrorMessage(response.error))
  }
}

const onCancel = () => {
  router.push({ name: 'listRelayer' })
}

const toggleModal = () => {
  isDeleteModalOpen.value = !isDeleteModalOpen.value
}

const saveState = async () => {
  await saveLastState({
    path: router.currentRoute.value.path,
    relayer: toRaw(relayer.value),
    selectedChainIds: JSON.stringify(toRaw(selectedChainIds.value)),
  })
}

const onAddChainId = async (chainIdObject?: string | ChainIdObject) => {
  if (!chainIdObject) {
    return
  }

  errors.value.chainIds = []

  const chainId =
    typeof chainIdObject === 'string' ? chainIdObject : chainIdObject.id

  if (typeof chainId === 'string' && !!numberOrHexValidator(chainId)) {
    return errors.value.chainIds.push(numberOrHexValidator(chainId))
  }

  const chainIdNumber = Number(chainId)

  const index = selectedChainIds.value.findIndex(
    chainIdObject_ => chainIdObject_.id === chainIdNumber
  )
  if (index > -1) {
    return errors.value.chainIds.push(t('input.validators.exists'))
  }

  selectedChainIds.value.push(createChainIdObject(chainIdNumber))
  await saveState()
}

const onRemoveChainId = async (chainIdObject: string | ChainIdObject) => {
  const chainIdHex =
    typeof chainIdObject === 'string' ? Number(chainIdObject) : chainIdObject.id

  const index = selectedChainIds.value.findIndex(
    chainIdObject_ => chainIdObject_.id === chainIdHex
  )
  selectedChainIds.value.splice(index, 1)
  await saveState()
}

const deleteRelayer = async () => {
  if (isEdit) {
    await GlobalDatabase.removeRelayer(relayerId)
  }
  router.push({ name: 'listRelayer' })
}

const validateNameUniqueness = (name: string) => {
  const relayerExist = allRelayers.value.find(relayer => relayer.name === name)

  if (relayerExist && (relayerExist.id !== relayerId || !isEdit)) {
    errors.value.name.push(t('add_relayer.unique_name_error'))
  }

  return true
}

const validateApiUrlUniqueness = (apiUrl: string) => {
  const normalizedApiUrl = normalizeUrl(apiUrl)
  const relayerExist = allRelayers.value.find(
    relayer => relayer.apiUrl === normalizedApiUrl
  )

  if (relayerExist && (relayerExist.id !== relayerId || !isEdit)) {
    errors.value.apiUrl.push(t('add_relayer.unique_api_url_error'))
  }

  return true
}

const normalizeUrl = (apiUrl: string) => removeTrailingSlash(apiUrl).trim()

onUnmounted(() => {
  saveLastState({
    relayer: undefined,
    selectedChainIds: undefined,
  })
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        isEdit
          ? (_openBlock(), _createElementBlock("h3", _hoisted_3, _toDisplayString(_ctx.$t('relayer.edit.heading')), 1 /* TEXT */))
          : (_openBlock(), _createElementBlock("h3", _hoisted_4, _toDisplayString(_ctx.$t('relayer.add.heading')), 1 /* TEXT */)),
        _createVNode(IconButton, { onOnClick: onCancel })
      ]),
      (!isEdit)
        ? (_openBlock(), _createBlock(Notification, {
            key: 0,
            class: "mb-4"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('relayer.add.warning')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }))
        : _createCommentVNode("v-if", true),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_6, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('relayer.add.name_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "name",
              ref_key: "name",
              ref: name,
              value: relayer.value.name,
              placeholder: _ctx.$t('relayer.add.name_placeholder'),
              autofocus: focus.value,
              "is-required": true,
              "validate-on-blur": false,
              errors: errors.value.name,
              "extra-classes": "input",
              onOnUpdate: _cache[0] || (_cache[0] = 
              value =>
                (relayer.value.name = value) &&
                validateNameUniqueness(value) &&
                saveState()
            ),
              onOnErrors: _cache[1] || (_cache[1] = newErrors => (errors.value.name = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "autofocus", "errors"]),
            (errors.value.name?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: errors.value.name[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('relayer.add.website_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "website",
              ref_key: "website",
              ref: website,
              value: relayer.value.website,
              placeholder: _ctx.$t('relayer.add.website_placeholder'),
              errors: errors.value.website,
              validators: [_unref(urlValidator)],
              "extra-classes": "input",
              onOnUpdate: _cache[2] || (_cache[2] = value => (relayer.value.website = value) && saveState()),
              onOnErrors: _cache[3] || (_cache[3] = newErrors => (errors.value.website = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (errors.value.website?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: errors.value.website[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_8, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('relayer.add.api_url_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(_unref(Input), {
              name: "apiUrl",
              ref_key: "apiUrl",
              ref: apiUrl,
              value: relayer.value.apiURL,
              placeholder: _ctx.$t('relayer.add.api_url_placeholder'),
              errors: errors.value.apiUrl,
              validators: [_unref(urlValidator)],
              "validate-on-blur": false,
              "is-required": true,
              "extra-classes": "input",
              onOnUpdate: _cache[4] || (_cache[4] = 
              value =>
                (relayer.value.apiURL = value) &&
                validateApiUrlUniqueness(value) &&
                saveState()
            ),
              onOnErrors: _cache[5] || (_cache[5] = newErrors => (errors.value.apiUrl = newErrors))
            }, null, 8 /* PROPS */, ["value", "placeholder", "errors", "validators"]),
            (errors.value.apiUrl?.length)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: errors.value.apiUrl[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true)
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createVNode(_unref(Label), {
              label: _ctx.$t('relayer.add.chain_ids_label')
            }, null, 8 /* PROPS */, ["label"]),
            _createVNode(DropdownMultiple, {
              "data-testid": "chains-dropdown",
              selected: selectedChainIds.value,
              options: allChains.value,
              "is-open-up": true,
              "can-add-tags": true,
              "option-id": "id",
              "option-label": "label",
              class: "is-family-monospace has-text-weight-bold",
              onOnAddOption: onAddChainId,
              onOnRemoveOption: onRemoveChainId,
              onOnAddTag: onAddChainId,
              onOnRemoveTag: onRemoveChainId
            }, null, 8 /* PROPS */, ["selected", "options"]),
            (errors.value.chainIds?.length && selectedChainIds.value.length > 0)
              ? (_openBlock(), _createBlock(_unref(Error), {
                  key: 0,
                  error: errors.value.chainIds[0]
                }, null, 8 /* PROPS */, ["error"]))
              : _createCommentVNode("v-if", true),
            (selectedChainIds.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(_ctx.$t('relayer.add.chain_ids_all_networks')), 1 /* TEXT */))
              : _createCommentVNode("v-if", true)
          ])
        ]),
        _createElementVNode("div", {
          class: _normalizeClass(["buttons mt-5", { 'three-buttons': isEdit }])
        }, [
          isEdit
            ? (_openBlock(), _createBlock(Button, {
                key: 0,
                variant: "secondary",
                "custom-class": "text-red-55 border-red-55 ",
                onClick: toggleModal
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('relayer.delete.trigger')), 1 /* TEXT */)
                ]),
                _: 1 /* STABLE */
              }))
            : _createCommentVNode("v-if", true),
          _createVNode(Button, {
            variant: isEdit ? 'secondary' : 'text',
            onClick: onCancel
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('relayer.add.close')), 1 /* TEXT */)
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["variant"]),
          _createVNode(Button, {
            variant: "primary",
            "is-full-width": true,
            "is-loading": isSubmitting.value,
            type: "submit",
            "data-testid": "add-relayer-submit",
            disabled: !canSubmit.value || isSubmitting.value,
            onClick: _withModifiers(save, ["stop"])
          }, {
            default: _withCtx(() => [
              _createVNode(Spinner, {
                class: _normalizeClass([{ 'hide-text': !isSubmitting.value }, "center-spinner"]),
                "is-dark": true
              }, null, 8 /* PROPS */, ["class"]),
              isEdit
                ? (_openBlock(), _createElementBlock("span", {
                    key: 0,
                    class: _normalizeClass({ 'hide-text': isSubmitting.value })
                  }, _toDisplayString(_ctx.$t(`relayer.edit.submit`)), 3 /* TEXT, CLASS */))
                : (_openBlock(), _createElementBlock("span", {
                    key: 1,
                    class: _normalizeClass({ 'hide-text': isSubmitting.value })
                  }, _toDisplayString(_ctx.$t(`relayer.add.submit`)), 3 /* TEXT, CLASS */))
            ]),
            _: 1 /* STABLE */
          }, 8 /* PROPS */, ["is-loading", "disabled"])
        ], 2 /* CLASS */)
      ])
    ]),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      _createVNode(ModalTransition, null, {
        default: _withCtx(() => [
          (isDeleteModalOpen.value)
            ? (_openBlock(), _createBlock(RelayerDeleteModal, {
                key: 0,
                "relayer-name": relayer.value.name,
                "on-confirm": deleteRelayer,
                "on-cancel": toggleModal
              }, null, 8 /* PROPS */, ["relayer-name"]))
            : _createCommentVNode("v-if", true)
        ]),
        _: 1 /* STABLE */
      })
    ]))
  ], 64 /* STABLE_FRAGMENT */))
}
}

})